import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { EDT_KEY } from '@teetsh/app/src/queries/edt/useEDTs';
import { EDTEvent, EmploiDuTemps } from '@teetsh/app/src/types/edt';
import { serializeEdtSeanceCreate } from '@teetsh/app/src/queries/edt/serializer';
import { addToEDTPast } from '@teetsh/app/src/queries/edt/historyAtoms';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

export default function useCreateEDTEvent(
  edtId: string,
  disableUndoRedo: boolean = false,
) {
  const { notifyError } = useNotification();
  const { t } = useTranslation(['emploiDuTemps']);
  const { schoolyear, school } = useSchoolSchoolyear();
  const user = useUser();
  const queryClient = useQueryClient();
  const requestURL = `/schoolyears/${schoolyear.id}/schools/${school.id}/emplois-du-temps/${edtId}/seances`;
  const [, addToPast] = useAtom(addToEDTPast);
  const { mutateAsync } = useMutation({
    mutationFn: (evt: EDTEvent) =>
      request(requestURL, {
        method: 'POST',
        body: serializeEdtSeanceCreate(evt, user.location),
      }),
    onMutate: (evt: EDTEvent) => {
      if (!disableUndoRedo) {
        const oldEDT = queryClient.getQueryData<EmploiDuTemps>([
          schoolyear.id,
          school.id,
          EDT_KEY,
          edtId,
        ]);
        if (oldEDT) {
          addToPast({ edtId: oldEDT.id, seances: oldEDT.events });
        }
      }
      queryClient.setQueryData<EmploiDuTemps>(
        [schoolyear.id, school.id, EDT_KEY, edtId],
        (oldEDT: EmploiDuTemps) => {
          const oldEvents = oldEDT?.events || [];
          return {
            ...oldEDT,
            events: [evt, ...oldEvents],
          };
        },
      );
    },
    onError: () => {
      notifyError({
        title: t('emploiDuTemps:messages.seance.createError'),
      });
    },
  });
  return mutateAsync;
}
